import React, { useMemo } from 'react'
import { Box, Flex, Text, Image, useDisclosure, PseudoBox } from '@chakra-ui/core'
import { IComment, RootState } from 'constants/interfaces'
import { useUserName } from 'hooks/useUserName'
import { DateText } from 'components/Elements'
import { calculateUserActivity, formatTimestamp, getTextDirection } from 'utils'
import { renderTextWithLineBreaks } from 'components/Post/PostTitle'
import { UploadedFileList } from 'components/UploadedFileList'
import { AudioPlayer } from 'components/AudioPlayer'
import colors from 'constants/colors'
import { CommentButton } from 'components/Post/CommentButton'
import { Post } from 'redux/reducers/feed'
import keys from 'constants/keys'
import { LikeButton } from 'components/Post/LikeButton'
import { ViewButton } from 'components/Post/ViewButton'
import { useDispatch, useSelector } from 'react-redux'
import LikesModal from 'components/Post/LikesModal'
import { ViewsModal } from 'components/Post/ViewsModal'
import { SecretID } from 'components/CommonComponents/SecretID'
import { PostMediaGrid } from 'components/NewsFeed/PostMediaGrid'
import { ReplyMessage } from './ReplyMessage'
import { SmileIcon } from 'lucide-react'
import { ReactionsPicker } from 'components/CommonComponents/Emojis'
import { useReactionsPicker } from 'hooks/useReactionsPicker'
import { updateComment } from 'redux/actions/comments'
import { MessageReactions } from './MessageReactions'
import useLocaleText from 'components/useLocaleText'
import { MessageMenu } from './MessageMenu'
import strings from 'constants/strings'

interface IChatMessage {
  message: IComment
  isUserMessage: boolean
  deleteCommentHandler: (cid: string) => void
  onChatClick?: (commentId: string) => void
  currentChat?: Post
  commentsCounter?: number
  commentsCounterLoading?: boolean
  viewedIds?: string[]
  reply_cid?: string
}
export const ChatMessage = ({
  message,
  isUserMessage,
  currentChat,
  deleteCommentHandler,
  onChatClick,
  commentsCounter = 0,
  commentsCounterLoading = false,
  reply_cid,
}: IChatMessage & {}) => {
  const dispatch = useDispatch()
  const { uid } = useSelector((state: RootState) => state.auth)
  const {
    encodedUID,
    config: { permission },
  } = useSelector((state: RootState) => state.config)
  const theme = useSelector((state: RootState) => state.general.theme)

  const { message: text, created_by, created_at_ts, videos, audios, images, files, ref_comment, reactions } = message

  const userName = useUserName(created_by)
  const { likes, views } = useSelector((state: RootState) => state.comments.commentsActivity[message.cid] || {})
  const { isOpen: isLikesModalOpen, onOpen: onLikesModalOpen, onClose: onCloseLikesModal } = useDisclosure()
  const { isOpen: isViewModalOpen, onOpen: onViewModalOpen, onClose: onCloseViewModal } = useDisclosure()

  const { isReactionPickerVisible, isFullPickerOpen, handleReactionButtonClick, handleEmojiSelect, openFullPicker } =
    useReactionsPicker({ messageId: message.cid })

  const t_edited = useLocaleText('edited')

  const onEmojiSelect = (emoji: string) => {
    if (!currentChat?.post_id) return

    handleEmojiSelect(emoji)
    dispatch(
      updateComment({
        oid: currentChat?.post_id,
        cid: message.cid,
        reaction: emoji,
        object_created_by: message.created_by,
        groups: currentChat?.groups,
        tags: currentChat?.tags,
        users: currentChat?.users,
        ...(reply_cid ? { reply_cid } : {}),
      })
    )
  }
  const likesCounter = useMemo(() => (likes && Object.keys(likes)?.length) ?? 0, [likes])
  const viewsCounter = useMemo(() => (views && Object.keys(views).length) ?? 0, [views])

  const users = Array.from(new Set([...(currentChat?.group_users ?? []), ...(currentChat?.post_origin_users ?? [])]))
  const viewed = useMemo(() => calculateUserActivity(users, views), [users, views])
  const liked = useMemo(() => calculateUserActivity(users, likes), [likes, users])

  const isAdminCommentsPermission = permission?.comments === keys.USER_ACTIONS_MAP.APP_ADMIN

  const isShowEmojiAndReplyPicker =
    currentChat?.type !== keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT ||
    ((currentChat?.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT && reply_cid) as boolean)

  const isMessageEdited = useMemo(() => message.updated_at_ts > message.created_at_ts, [message])

  const outputFormat = theme?.is_support_us_date_format
    ? strings.DATE_FORMATS.outputs.CREATED_AT_TS_US_FORMAT
    : strings.DATE_FORMATS.outputs.CREATED_AT_TS_FORMAT

  return (
    <>
      <Box position="relative" w="100%" role="group">
        {isShowEmojiAndReplyPicker && (
          <PseudoBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            w="20px"
            h="20px"
            borderRadius="50%"
            bg={colors.greyLight}
            top="50%"
            transform="translateY(-50%)"
            right={isUserMessage ? 'auto' : '-28px'}
            left={isUserMessage ? '-28px' : 'auto'}
            zIndex={1}
            cursor="pointer"
            onClick={handleReactionButtonClick}
            _groupHover={{ opacity: 1 }}
            _hover={{ opacity: 1 }}
            opacity={0}
            transition="opacity 0.2s ease-in-out"
          >
            <SmileIcon size={20} color="gray" />
          </PseudoBox>
        )}

        {isReactionPickerVisible && (
          <Box
            position="absolute"
            zIndex={10}
            right={isUserMessage ? 'auto' : '-5px'}
            left={isUserMessage ? '-5px' : 'auto'}
            top="50%"
            transform="translateY(-50%)"
            onClick={(e) => e.stopPropagation()}
          >
            <ReactionsPicker
              onEmojiSelect={onEmojiSelect}
              openFullPicker={openFullPicker}
              isFullPickerOpen={isFullPickerOpen}
            />
          </Box>
        )}

        <Flex
          w="100%"
          onClick={() => onChatClick?.(message.cid)}
          bg={isUserMessage ? 'blue.200' : '#ffe6f2'}
          flexDir="column"
          alignItems={isUserMessage ? 'flex-end' : 'flex-start'}
          borderBottomRightRadius="20px"
          borderBottomLeftRadius="20px"
          borderTopLeftRadius={isUserMessage ? '20px' : '0'}
          borderTopRightRadius={isUserMessage ? '0' : '20px'}
          p="10px"
          mt="10px"
          css={isReactionPickerVisible ? { filter: 'brightness(0.85)' } : {}}
        >
          {ref_comment && <ReplyMessage replyMessage={ref_comment} isUserMessage={isUserMessage} />}
          <Flex
            alignItems="center"
            minW="250px"
            w="100%"
            flexDir={isUserMessage ? 'row-reverse' : 'row'}
            justifyContent="space-between"
          >
            <Text fontSize="14px" fontWeight="600" color="black" maxW="85%">
              {userName}
            </Text>

            <MessageMenu
              isUserMessage={isUserMessage}
              isAdminCommentsPermission={isAdminCommentsPermission}
              deleteCommentHandler={deleteCommentHandler}
              isShowEmojiAndReplyPicker={isShowEmojiAndReplyPicker ?? false}
              cid={message.cid}
            />
          </Flex>
          {!!currentChat && currentChat.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT && (
            <Flex
              justifyContent="space-between"
              flexDir={isUserMessage ? 'row-reverse' : 'row'}
              alignItems="center"
              w="100%"
            >
              <Flex alignItems="center" style={{ columnGap: '5px' }} flexDir={isUserMessage ? 'row-reverse' : 'row'}>
                <DateText>{formatTimestamp(created_at_ts, outputFormat)}</DateText>
                <SecretID value={encodedUID} />
              </Flex>
              {isMessageEdited && (
                <Text fontSize="12px" color="#90949C">
                  {t_edited}
                </Text>
              )}
            </Flex>
          )}
          <Text
            my="5px"
            wordBreak={'break-word'}
            fontFamily="inherit"
            fontSize="14px"
            minW="40px"
            w="100%"
            dir={getTextDirection(text)}
          >
            {text !== '' && renderTextWithLineBreaks({ text })}
          </Text>
          {videos &&
            videos.length > 0 &&
            videos.map((video) => {
              return (
                <Flex key={video} my="3" maxHeight="200px" objectFit="contain">
                  <video src={video} controls />
                </Flex>
              )
            })}
          {images && images.length > 0 && (
            <Box px="5px" py="5px" w="100%">
              <PostMediaGrid px="0" media={images} isEditable={false}>
                {images !== null && images?.map((imageSrc) => <Image key={imageSrc} src={imageSrc} width="100%" />)}
              </PostMediaGrid>
            </Box>
          )}

          {audios && audios.map((audioURI) => <AudioPlayer key={audioURI} src={audioURI} />)}
          {files && <UploadedFileList fileList={files} />}
          {!!currentChat && currentChat.type !== keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT && (
            <Flex
              justifyContent="space-between"
              flexDir={isUserMessage ? 'row-reverse' : 'row'}
              alignItems="center"
              w="100%"
            >
              <Flex alignItems="center" style={{ columnGap: '5px' }} flexDir={isUserMessage ? 'row-reverse' : 'row'}>
                <DateText>{formatTimestamp(created_at_ts, outputFormat)}</DateText>
                <SecretID value={encodedUID} />
              </Flex>
              {isMessageEdited && (
                <Text fontSize="12px" color="#90949C">
                  {t_edited}
                </Text>
              )}
            </Flex>
          )}
          {currentChat?.type === keys.POST_TYPE.GROUP_POST_ANNOUNCEMENT && !reply_cid && (
            <Flex w="100%" justifyContent="flex-end">
              <LikeButton
                created_by={message.created_by}
                likes_counter={likesCounter}
                liked={liked}
                isShowLikeButton={message.created_by !== uid}
                isLikedPost={likes?.[uid!] ?? false}
                isDoublePost={false}
                isAllPostCollapsed={true}
                handleLikesModal={onLikesModalOpen}
              />

              <ViewButton
                viewed={viewed}
                views_counter={viewsCounter}
                created_by={message.created_by}
                isShowViewIcon={message.created_by !== uid}
                isViewed={views?.[uid!] ?? false}
                isDoublePost={false}
                isAllPostCollapsed={true}
                openModal={onViewModalOpen}
              />

              <CommentButton
                postId={message.cid}
                commentsCounterLoading={commentsCounterLoading}
                showCommentsSection={false}
                commentsCounter={commentsCounter}
                isViewed={true}
                isDoublePost={false}
                isAllPostCollapsed={true}
              />
            </Flex>
          )}
        </Flex>

        {reactions && Object.keys(reactions).length > 0 && (
          <Box
            position="absolute"
            bottom="-15px"
            left={isUserMessage ? 'unset' : '10px'}
            right={isUserMessage ? '10px' : 'unset'}
            zIndex={10}
          >
            <MessageReactions reactions={reactions} />
          </Box>
        )}
      </Box>
      {isLikesModalOpen && (
        <LikesModal
          isOpen={isLikesModalOpen}
          onClose={onCloseLikesModal}
          likes_counter={likesCounter}
          created_by={message.created_by}
          liked={liked}
          isShowPings={false}
        />
      )}
      {isViewModalOpen && (
        <ViewsModal
          isOpen={isViewModalOpen}
          onClose={onCloseViewModal}
          views_counter={viewsCounter}
          created_by={message.created_by}
          viewed={viewed}
        />
      )}
    </>
  )
}
